import { useEffect, useMemo } from 'react';
import elevenlabsLogo from '@/assets/images/elevenlabs_logo.svg';
import useIntegrationsStore from '@/stores/IntegrationsStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';
import { useShallow } from 'zustand/react/shallow';

import { saveIntegrationSettings } from '@/hooks/integrations';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';

const ElevenLabsSettings = () => {
  const {
    elevenlabs,
    loading: storeLoading,
    setElevenlabs,
  } = useIntegrationsStore(
    useShallow((state) => ({
      elevenlabs: state.elevenlabs,
      loading: state.loading,
      setElevenlabs: state.setElevenlabs,
    }))
  );

  const hasApiKey = elevenlabs?.has_api_key === true;

  // Zod schema for ElevenLabs settings with conditional validation
  const elevenLabsSchema = useMemo(
    () =>
      z
        .object({
          api_key: z.string().optional(),
          voice_id: z.string().min(1, 'Voice ID is required'),
          similarity_boost: z.number().min(0).max(1).optional().nullable(),
          style: z.number().min(0).max(1).optional().nullable(),
          speed: z.number().min(0.7).max(1.2).optional().nullable(),
        })
        .refine(
          (data) => {
            // If no API key exists, api_key is required
            if (!hasApiKey && !data.api_key) {
              return false;
            }
            return true;
          },
          {
            message: 'API key is required',
            path: ['api_key'],
          }
        ),
    [hasApiKey]
  );

  const form = useForm({
    resolver: zodResolver(elevenLabsSchema),
    defaultValues: {
      api_key: '',
      voice_id: '',
      similarity_boost: null,
      style: null,
      speed: null,
    },
  });

  // Determine connection status based on elevenlabs data
  const getConnectionStatus = () => {
    if (!elevenlabs) {
      return 'not_connected';
    }

    const hasApiKey = elevenlabs.has_api_key === true;
    const isActive = elevenlabs.active === true;

    // Connected: has_api_key is true and active is true
    if (hasApiKey && isActive) {
      return 'connected';
    }

    // Not connected: otherwise
    return 'not_connected';
  };

  const connectionStatus = getConnectionStatus();

  // Initialize form data from store
  useEffect(() => {
    if (elevenlabs) {
      form.reset({
        api_key: '',
        voice_id: elevenlabs.voice_id || '',
        similarity_boost: elevenlabs.similarity_boost ?? null,
        style: elevenlabs.style ?? null,
        speed: elevenlabs.speed ?? null,
      });
    }
  }, [elevenlabs, form]);

  const onSubmit = async (values) => {
    try {
      const settings = {
        ...(values.api_key && { api_key: values.api_key }),
        voice_id: values.voice_id,
        ...(values.similarity_boost !== null &&
          values.similarity_boost !== undefined && {
            similarity_boost: values.similarity_boost,
          }),
        ...(values.style !== null &&
          values.style !== undefined && {
            style: values.style,
          }),
        ...(values.speed !== null &&
          values.speed !== undefined && {
            speed: values.speed,
          }),
      };

      await saveIntegrationSettings('elevenlabs', settings);
      toast.success('ElevenLabs settings saved successfully');

      // Update store directly with new values
      if (elevenlabs) {
        setElevenlabs({
          ...elevenlabs,
          voice_id: values.voice_id,
          has_api_key: values.api_key ? true : elevenlabs.has_api_key,
          active: values.api_key ? true : elevenlabs.active,
          ...(values.similarity_boost !== null &&
            values.similarity_boost !== undefined && {
              similarity_boost: values.similarity_boost,
            }),
          ...(values.style !== null &&
            values.style !== undefined && {
              style: values.style,
            }),
          ...(values.speed !== null &&
            values.speed !== undefined && {
              speed: values.speed,
            }),
        });
      }

      // Clear API key field after successful save (for security)
      form.reset({
        api_key: '',
        voice_id: values.voice_id,
        similarity_boost: values.similarity_boost,
        style: values.style,
        speed: values.speed,
      });
    } catch (error) {
      console.error('Failed to save ElevenLabs settings:', error);
      toast.error(
        error.response?.data?.message || 'Failed to save ElevenLabs settings'
      );
    }
  };

  if (storeLoading) {
    return (
      <div className='flex items-center justify-center pt-48'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  return (
    <div className='h-full space-y-6'>
      <div className='flex justify-between'>
        <div>
          <h3 className='text-lg font-medium'>ElevenLabs Integration</h3>
          <p className='text-sm text-muted-foreground'>
            Configure your ElevenLabs API settings for text-to-speech
          </p>
        </div>
      </div>
      <Separator />

      <div className='space-y-6'>
        {/* Connection Status */}
        <Card>
          <CardHeader>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                <div className='rounded-lg bg-gray-100 p-2'>
                  <img
                    src={elevenlabsLogo}
                    alt='ElevenLabs'
                    className='h-6 w-6 object-contain'
                  />
                </div>
                <div>
                  <CardTitle>ElevenLabs</CardTitle>
                  <CardDescription>
                    {connectionStatus === 'connected'
                      ? 'Connected'
                      : 'Not Connected'}
                  </CardDescription>
                </div>
              </div>
              {connectionStatus === 'connected' ? (
                <div className='flex items-center gap-2 rounded-full bg-green-50 px-3 py-1'>
                  <CheckCircle2 className='h-4 w-4 text-green-600' />
                  <span className='text-sm font-medium text-green-600'>
                    Connected
                  </span>
                </div>
              ) : (
                <div className='flex items-center gap-2 rounded-full bg-gray-50 px-3 py-1'>
                  <XCircle className='h-4 w-4 text-gray-400' />
                  <span className='text-sm font-medium text-gray-400'>
                    Not Connected
                  </span>
                </div>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Settings Form */}
        <Card>
          <CardHeader>
            <CardTitle>API Configuration</CardTitle>
            <CardDescription>
              Enter your ElevenLabs API credentials and settings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className='space-y-4'
              >
                {elevenlabs?.api_key_masked && (
                  <Alert>
                    <AlertDescription>
                      API Key is configured: {elevenlabs.api_key_masked}
                    </AlertDescription>
                  </Alert>
                )}

                <FormField
                  control={form.control}
                  name='api_key'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        API Key{' '}
                        {!elevenlabs?.has_api_key && (
                          <span className='text-red-500'>*</span>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type='password'
                          placeholder={
                            elevenlabs?.has_api_key
                              ? 'Enter new API key to update'
                              : 'Enter your ElevenLabs API key'
                          }
                          {...field}
                        />
                      </FormControl>
                      {elevenlabs?.has_api_key && (
                        <p className='text-xs text-muted-foreground'>
                          Leave blank to keep current API key
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='voice_id'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Voice ID <span className='text-red-500'>*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder='Enter Voice ID' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Similarity Boost - only show if exists in store */}
                {elevenlabs?.similarity_boost !== null &&
                  elevenlabs?.similarity_boost !== undefined && (
                    <FormField
                      control={form.control}
                      name='similarity_boost'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Similarity Boost: {(field.value ?? 0).toFixed(2)}
                          </FormLabel>
                          <FormControl>
                            <Slider
                              min={0}
                              max={1}
                              step={0.1}
                              value={[field.value ?? 0]}
                              onValueChange={(value) =>
                                field.onChange(value[0])
                              }
                              className='w-full max-w-md'
                            />
                          </FormControl>
                          <p className='text-xs text-muted-foreground'>
                            Controls how closely the voice matches the original
                            (0.0 - 1.0)
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                {/* Style - only show if exists in store */}
                {elevenlabs?.style !== null &&
                  elevenlabs?.style !== undefined && (
                    <FormField
                      control={form.control}
                      name='style'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Style: {(field.value ?? 0).toFixed(2)}
                          </FormLabel>
                          <FormControl>
                            <Slider
                              min={0}
                              max={1}
                              step={0.1}
                              value={[field.value ?? 0]}
                              onValueChange={(value) =>
                                field.onChange(value[0])
                              }
                              className='w-full max-w-md'
                            />
                          </FormControl>
                          <p className='text-xs text-muted-foreground'>
                            Controls the style of the voice (0.0 - 1.0)
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                {/* Speed - only show if exists in store */}
                {elevenlabs?.speed !== null &&
                  elevenlabs?.speed !== undefined && (
                    <FormField
                      control={form.control}
                      name='speed'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Speed: {(field.value ?? 1.0).toFixed(2)}
                          </FormLabel>
                          <FormControl>
                            <Slider
                              min={0.7}
                              max={1.2}
                              step={0.1}
                              value={[field.value ?? 1.0]}
                              onValueChange={(value) =>
                                field.onChange(value[0])
                              }
                              className='w-full max-w-md'
                            />
                          </FormControl>
                          <p className='text-xs text-muted-foreground'>
                            Controls the speed of the voice (0.7 - 1.2)
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                <Button type='submit' disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? (
                    <>
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ElevenLabsSettings;
